import clsx from 'clsx'
import type { ReactElement } from 'react'
import {
  cellContentMain,
  cellContentRowMain
} from '~/components/DataTable/DataTable.css'
import type { FiltersState } from '~/components/Filters/FilterBar/types'
import { checkboxStyle } from '~/components/Form/Input/Checkbox.css'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { renderPhoneLink } from '~/components/Scopes/Tables/phoneLink'
import { useCompanyContacts } from '~/domains/companies/queries/companyContacts'
import { useConfig } from '~/domains/configuration/queries/config'
import { ContactRowActions } from '~/domains/contacts/components/ContactRowActions/ContactRowActions'
import { ContactTypeBadges } from '~/domains/contacts/components/ContactTypeBadges'
import { formatDateTime } from '~/utils/date'
import { formatName } from '~/utils/names'
import {
  panel,
  panelAllLink,
  panelHeader,
  panelTitle,
  subTable,
  subTableActions,
  subTableCell,
  subTableColActions,
  subTableColCheckbox,
  subTableColContact,
  subTableColLastAction,
  subTableColPhone,
  subTableColTypes,
  subTableHeader,
  subTableMuted,
  subTableRow,
  subTableScroll,
  subTableWrap
} from './CompanyContactsSubTable.css'

type CompanyContactsSubTableProps = {
  companyId: number
  companyUrl: string
  filters: FiltersState
  selectedContactIds: number[]
  onToggleContact: (contactId: number, companyId: number) => void
}

export const CompanyContactsSubTable = ({
  companyId,
  companyUrl,
  filters,
  selectedContactIds,
  onToggleContact
}: CompanyContactsSubTableProps): ReactElement => {
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const {
    data: contacts,
    isLoading,
    isError
  } = useCompanyContacts(companyId, filters)
  const contactUrl = (contactId: number) =>
    `${legacyUrl}/admin/entreprises/edit/${companyId}#contacts/${contactId}`

  if (isLoading) {
    return <Loader small />
  }

  if (isError) {
    return (
      <NoResult isSmall isError message="Impossible de charger les contacts" />
    )
  }

  if (contacts.length === 0) {
    return <NoResult isSmall message="Aucun contact correspondant" />
  }

  return (
    <div className={panel}>
      <div className={panelHeader}>
        <span className={panelTitle}>
          {contacts.length > 1
            ? `${contacts.length} contacts correspondants`
            : '1 contact correspondant'}
        </span>
        <a
          href={companyUrl}
          target="_blank"
          rel="noreferrer"
          className={panelAllLink}
        >
          Voir tous les contacts →
        </a>
      </div>
      <div className={subTableScroll}>
        <table className={subTable}>
          <colgroup>
            <col className={subTableColCheckbox} />
            <col className={subTableColContact} />
            <col />
            <col className={subTableColTypes} />
            <col className={subTableColPhone} />
            <col className={subTableColLastAction} />
            <col className={subTableColActions} />
          </colgroup>
          <thead>
            <tr>
              <th className={subTableHeader} aria-label="Sélection" />
              <th className={subTableHeader}>Contact</th>
              <th className={subTableHeader}>Fonction</th>
              <th className={subTableHeader}>Type</th>
              <th className={subTableHeader}>Téléphone</th>
              <th className={subTableHeader}>Dernière action</th>
              <th
                className={clsx(subTableHeader, subTableActions)}
                aria-label="Actions"
              />
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => (
              <tr key={contact.id} className={subTableRow}>
                <td className={subTableCell}>
                  <input
                    type="checkbox"
                    className={checkboxStyle}
                    aria-label="Sélectionner le contact"
                    checked={selectedContactIds.includes(contact.id)}
                    onChange={() => onToggleContact(contact.id, companyId)}
                  />
                </td>
                <td className={subTableCell}>
                  <a
                    href={contactUrl(contact.id)}
                    target="_blank"
                    rel="noreferrer"
                    className={clsx(cellContentRowMain, cellContentMain)}
                  >
                    {formatName(contact) || '—'}
                  </a>
                </td>
                <td
                  className={subTableCell}
                  title={contact.role?.name || contact.customRole || undefined}
                >
                  {contact.role?.name || contact.customRole || '—'}
                </td>
                <td className={clsx(subTableCell, subTableWrap)}>
                  <ContactTypeBadges contact={contact} />
                </td>
                <td className={subTableCell}>
                  {renderPhoneLink(contact.phoneMobile || contact.phone) ?? '—'}
                </td>
                <td className={subTableCell}>
                  {contact.lastActionAt ? (
                    formatDateTime(new Date(contact.lastActionAt), {
                      dateStyle: 'short'
                    })
                  ) : (
                    <span className={subTableMuted}>Aucune</span>
                  )}
                </td>
                <td className={clsx(subTableCell, subTableActions)}>
                  <ContactRowActions
                    contactId={contact.id}
                    companyId={companyId}
                    contactUrl={contactUrl(contact.id)}
                    email={contact.email}
                    phoneMobile={contact.phoneMobile}
                    acceptsMessaging={contact.acceptsMessaging}
                    validPhoneMobile={contact.validPhoneMobile}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
